import Big from "big.js"
import * as z from "zod"

import { createEntityFor, ISODate } from "@axtesys/react-tools"

import { ReceiptOutput, ReceiptType } from "../api/graphql/types"
import { Euros } from "../lib/Money"
import { CashRegisterData, CashRegisterDataEntity } from "./CashRegister"
import { CompanyData, CompanyDataEntity } from "./Company"
import { BillingAddressData, BillingAddressDataEntity } from "./Contact"
import { DepartmentData, DepartmentDataEntity } from "./Department"
import { CartDiscountHistoryEntry } from "./Discount"
import {
  GPtomReceiptData,
  GPtomReceiptDataEntity,
  GPtomResponse,
} from "./GPtom"
import {
  HobexReceiptData,
  HobexReceiptDataEntity,
  HobexResponse,
} from "./Hobex"
import { InvoiceItem, OutputInvoiceItem } from "./InvoiceItem"
import {
  OperatorReceiptData as OperatorData,
  OperatorReceiptDataEntity,
} from "./Operator"
import {
  DetailedReceiptItem,
  ReceiptItem,
  ReceiptItemEntity,
} from "./ReceiptItem"
import { PrinterId } from "./ReceiptPrinter"
import {
  ShippingAddressData,
  ShippingAddressDataEntity,
} from "./ShippingAddress"
import {
  TaxInfo,
  TaxInfoEntity,
  TotalTaxInfo,
  TotalTaxInfoEntity,
} from "./TaxInfo"
import {
  DisplayTipTransfer,
  DisplayTipTransferEntity,
  TipTransfer,
} from "./TipTransfer"
import {
  DisplayValueTransfer,
  DisplayValueTransferEntity,
  ValueTransfer,
} from "./ValueTransfer"

export type ReceiptId = string

type SharedReceipt = {
  receiptId: ReceiptId

  // Info about the origin of the receipt
  company: CompanyData
  cashRegister: CashRegisterData
  operator: OperatorData

  // The assigned receipt number of the receipt
  receiptNumber: string

  // The entire shopping cart changelog.
  // Tracks all additions, changes and therefore voids.
  // Implemented for the cash register
  // ledger/journal/protocol features,
  // which require full action tracking per cash register.
  itemsHistory: InvoiceItem[]

  // All non-zero/non-voided items that were sold,
  // including item discounts and (proportional) cart discounts.
  items: OutputInvoiceItem[]

  // The entire cart discounts changelog.
  // Tracks all movements in regard to cart discounts.
  cartDiscountsHistory: CartDiscountHistoryEntry[]

  // Ways in which the final customer paid the company
  payments: ValueTransfer[]

  // Information about a potential contact/client assignment
  billingAddress?: BillingAddressData
  department?: DepartmentData
  shippingAddress?: ShippingAddressData
  receiptText?: string

  // Tip(s) from a final customer that should be
  // awarded to an operator for his/her good service.
  tips?: TipTransfer[]

  // NOTES:
  // 1. Ways in which the company paid the final customer.
  // 2. Former payments are inverted in case of a refund or cancel receipt.
  payouts?: ValueTransfer[]
}

export type InputReceipt = SharedReceipt & {
  // The printer that should be used to print the receipt.
  printerId: PrinterId

  // Rounding difference that
  // can occur when cart discounts are applied.
  roundingDifference?: Big
}

export type OutputReceipt = SharedReceipt & {
  // Generation information of the receipt
  createdAt: ISODate
  receivedAs: ReceiptOutput

  // Used for the cash register ledger/journal/protocol features.
  // All zero-amount/voided items that
  // were added and updated during the shopping cart manipulation.
  zeroAndVoidedItems: OutputInvoiceItem[]

  // Aggregated tax information per tax rate
  taxInfos: TaxInfo[]

  // Total tax information sums including
  // net, tax, gross, discount, undiscountedGross and correctedGross
  totalTaxInfo: TotalTaxInfo

  // True, if no signature from A-Trust could be retrieved.
  // Either because our backend is somehow broken/offline OR
  // their server(s) failed.
  signatureDeviceFailed: boolean

  // Card transaction information
  hobexResponse?: HobexResponse
  gpTomResponse?: GPtomResponse

  // Machine-readable signature representation
  // (retrievable by the final costumer via QR code OR RKSV URL)
  qrCodeRepresentation?: string

  // RKSV URL hash for retrieving the QR code signature
  // via a REST API endpoint that returns a JSON response.
  signatureUrlHash?: string
}

// Because of requirement changes (e.g. fraud protection)
// the `createdAt` timestamp of the receipt is now generated
// when the submit button in the `PaymentScreen` is used.
//
// Previous behaviour: As soon as the navigation took place from,
// `ShoppingCartScreen` to `PaymentScreen`
// the `createdAt` timestamp was created.
export type PreOutputReceipt = Omit<OutputReceipt, "createdAt">

type Receipt = Omit<
  OutputReceipt,
  | "cashRegister"
  | "operator"
  | "itemsHistory"
  | "items"
  | "zeroAndVoidedItems"
  | "cartDiscountsHistory"
> & {
  items: ReceiptItem[]

  cashRegister?: CashRegisterData
  operator?: OperatorData

  receiptType?: ReceiptType

  gpTomReceiptData?: GPtomReceiptData
  hobexReceiptData?: HobexReceiptData

  refundsReceiptNumber?: string
  refundedByReceiptNumber?: string
  cancelledByReceiptNumber?: string
}

export type DisplayReceipt = Omit<Receipt, "payments" | "tips" | "payouts"> & {
  payments: DisplayValueTransfer[]

  tips?: DisplayTipTransfer[]
  payouts?: DisplayValueTransfer[]
}

export type DetailedItemReceipt = Omit<Receipt, "items"> & {
  items: DetailedReceiptItem[]
}

export type SharedGenericReceipt = {
  id: string
  receiptNumber: string
  createdAt: ISODate
  valueTransferNames: string
  valueTransfers: string[]
  sumTotal: Euros

  cashRegisterName?: string
  operatorName?: string
}

export const DisplayReceiptEntity = createEntityFor<
  DisplayReceipt | undefined
>()
  .withSchema(
    z.optional(
      z.object({
        receiptId: z.string(),
        company: CompanyDataEntity.schema,

        createdAt: z.string(),
        receiptNumber: z.string(),
        receivedAs: z.enum(["Email", "NoOutput", "Paper"]),

        items: z.array(ReceiptItemEntity.schema),

        payments: z.array(DisplayValueTransferEntity.schema),
        taxInfos: z.array(TaxInfoEntity.schema),
        totalTaxInfo: TotalTaxInfoEntity.schema,

        signatureDeviceFailed: z.boolean(),

        cashRegister: z.optional(CashRegisterDataEntity.schema),
        operator: z.optional(OperatorReceiptDataEntity.schema),

        receiptType: z.optional(
          z.enum([
            "DEFAULT",
            "REFUND",
            "CANCEL",
            "TRAINING",
            "ZERO",
            "START",
            "END",
            "REACTIVATE",
            "MONTHLY",
            "YEARLY",
          ]),
        ),

        billingAddress: z.optional(BillingAddressDataEntity.schema),
        department: z.optional(DepartmentDataEntity.schema),
        shippingAddress: z.optional(ShippingAddressDataEntity.schema),
        receiptText: z.ostring(),

        tips: z.optional(z.array(DisplayTipTransferEntity.schema)),
        payouts: z.optional(z.array(DisplayValueTransferEntity.schema)),

        hobexReceiptData: z.optional(HobexReceiptDataEntity.schema),
        gpTomReceiptData: z.optional(GPtomReceiptDataEntity.schema),

        qrCodeRepresentation: z.ostring(),
        signatureUrlHash: z.ostring(),

        refundsReceiptNumber: z.ostring(),
        refundedByReceiptNumber: z.ostring(),
        cancelledByReceiptNumber: z.ostring(),
      }),
    ),
  )
  .serialize(data => {
    if (!data) return undefined

    const items = data.items.map(ReceiptItemEntity.serialize)
    const payments = data.payments.map(DisplayValueTransferEntity.serialize)
    const taxInfos = data.taxInfos.map(TaxInfoEntity.serialize)
    const totalTaxInfo = TotalTaxInfoEntity.serialize(data.totalTaxInfo)

    const tips = data.tips?.map(DisplayTipTransferEntity.serialize)
    const payouts = data.payouts?.map(DisplayValueTransferEntity.serialize)

    return { ...data, items, payments, taxInfos, totalTaxInfo, tips, payouts }
  })
  .deserialize(json => {
    if (!json) return undefined

    const items = json.items.map(ReceiptItemEntity.deserialize)
    const payments = json.payments.map(DisplayValueTransferEntity.deserialize)
    const taxInfos = json.taxInfos.map(TaxInfoEntity.deserialize)
    const totalTaxInfo = TotalTaxInfoEntity.deserialize(json.totalTaxInfo)

    const tips = json.tips?.map(DisplayTipTransferEntity.deserialize)
    const payouts = json.payouts?.map(DisplayValueTransferEntity.deserialize)

    return { ...json, items, payments, taxInfos, totalTaxInfo, tips, payouts }
  })
